import { median, std } from './math';

import type { Clip } from './types';

/**
 * Autocorrelation (ACF) pitch detection. Returns frequency in Hz, or -1 if
 * the buffer is too quiet / unvoiced. `b` is a time-domain sample buffer.
 */
export const autoCorrelate = (b: Float32Array, sr: number): number => {
  let SIZE = b.length;
  let rms = 0;
  for (let i = 0; i < SIZE; i++) rms += b[i] * b[i];
  rms = Math.sqrt(rms / SIZE);
  if (rms < 0.01) return -1;

  let r1 = 0;
  let r2 = SIZE - 1;
  const thr = 0.2;
  for (let i = 0; i < SIZE / 2; i++) {
    if (Math.abs(b[i]) < thr) {
      r1 = i;
      break;
    }
  }
  for (let i = 1; i < SIZE / 2; i++) {
    if (Math.abs(b[SIZE - i]) < thr) {
      r2 = SIZE - i;
      break;
    }
  }

  const buf2 = b.slice(r1, r2);
  SIZE = buf2.length;
  const c = new Array(SIZE).fill(0);
  for (let i = 0; i < SIZE; i++)
    for (let j = 0; j < SIZE - i; j++) c[i] += buf2[j] * buf2[j + i];

  let d = 0;
  while (d < SIZE - 1 && c[d] > c[d + 1]) d++;
  let maxv = -1;
  let maxp = -1;
  for (let i = d; i < SIZE; i++) {
    if (c[i] > maxv) {
      maxv = c[i];
      maxp = i;
    }
  }

  let T0 = maxp;
  if (T0 > 0 && T0 < SIZE - 1) {
    const x1 = c[T0 - 1];
    const x2 = c[T0];
    const x3 = c[T0 + 1];
    const a = (x1 + x3 - 2 * x2) / 2;
    const bb = (x3 - x1) / 2;
    if (a) T0 = T0 - bb / (2 * a);
  }
  return T0 > 0 ? sr / T0 : -1;
};

/**
 * A frame further than this from the clip's median is a DETECTOR error, not a
 * pitch the singer produced — an octave (1200¢) or a harmonic (1902¢ for 3f)
 * the estimator locked onto for one window. Half an octave is the line: real
 * wobble inside a held vowel never reaches it, and every misdetection the
 * estimators produce (½f, 2f, 3f, 4f) lands outside it.
 */
const DETECTOR_OUTLIER_CENTS = 600;

/**
 * Whether a clip is a valid sustained voicing — knocks/noise have unstable
 * pitch and are filtered out (cent std must stay under 200).
 *
 * **Octave misdetections are dropped before the spread is measured.** The raw
 * `f0Hz` series this reads keeps them by contract: the engine's octave-jump
 * suppression produces a SEPARATE display-only `smoothedF0Hz`, and raw "is left
 * intact" (`libs/pitch-core/include/muzly_pitch.h` §raw vs smoothed). Counting
 * them as spread makes this gate fail at a rate the singer cannot influence —
 * with p of the frames an octave off, std ≈ sqrt(p(1-p))·1200, so **3% of
 * misdetected frames alone clears the 200¢ line** and the whole diagnosis is
 * thrown away with "your voice was not stable enough". The screen meanwhile
 * draws the suppressed series and looks correct
 * (`apps/web/.../capture/livePoints.ts`), so the message contradicts what the
 * user just watched. Dropping them keeps this gate measuring the voice.
 *
 * Knocks and noise still fail, and that is what the **majority** condition
 * defends: a clip is only a held note if the pitch around the median owns most
 * of it. Misdetection is a minority event (the estimators lock onto the note far
 * more often than onto its octave); a clip that splits evenly between two
 * pitches has no note to measure, and neither does noise, which scatters so wide
 * that the kept frames are a small share of a wandering series.
 *
 * The majority is counted, not assumed from the drop window: `median` is the
 * arithmetic middle, so on a clip split across an octave the ±600¢ window sits
 * off-centre (110/220 Hz → median 165 → +498¢ and −702¢) and keeps one side.
 * Half is then not enough.
 */
export const isValidVoice = (
  clip: Pick<Clip, 'pitches'> | null | undefined,
): boolean => {
  if (!clip || !clip.pitches || clip.pitches.length < 10) return false;
  const med = median(clip.pitches);
  if (!(med > 0)) return false;
  const cents = clip.pitches
    .map((f) => 1200 * Math.log2(f / med))
    .filter((c) => Math.abs(c) <= DETECTOR_OUTLIER_CENTS);
  if (cents.length < 10) return false;
  if (cents.length * 2 <= clip.pitches.length) return false;
  return std(cents) < 200;
};
